<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Projects</h1>
            <div class="px-4 sm:px-6 lg:px-8">
                <div
                    v-if="!loading && hasResults"
                    class="sm:flex sm:items-center"
                >
                    <div class="sm:flex sm:space-x-4">
                        <h1 class="text-2xl font-medium text-taupe-800">
                            Contexts
                        </h1>
                    </div>
                </div>

                <zen-container>
                    <zen-loading-indicator v-if="loading" />

                    <zen-empty-state
                        v-if="!loading && !hasResults"
                        title="No contexts yet"
                        description="Contexts are created automatically the first time your SDK sends an evaluation request. Connect your SDK and make your first evaluation call to see contexts appear here."
                    >
                        <template #icon>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1"
                                stroke="currentColor"
                                class="h-20 w-20"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                                />
                            </svg>
                        </template>
                    </zen-empty-state>

                    <zen-grid v-if="!loading && hasResults">
                        <zen-grid-table>
                            <zen-grid-header>
                                <zen-grid-header-row>
                                    <zen-grid-header-cell
                                        :hidable="false"
                                        title="Name"
                                    />
                                    <zen-grid-header-cell
                                        alignment="center"
                                        title="Type"
                                    />
                                    <zen-grid-header-cell title="Identifier" />
                                    <zen-grid-header-cell
                                        alignment="center"
                                        title="Segments"
                                    />
                                    <zen-grid-header-cell title="Last Seen" />
                                </zen-grid-header-row>
                            </zen-grid-header>
                            <zen-grid-body
                                :results="results"
                                :loading="loading"
                            >
                                <zen-grid-body-row
                                    v-for="result in sortedAndFilteredResults"
                                    :key="result.ulid"
                                >
                                    <zen-grid-body-cell
                                        :hidable="false"
                                        :primary="true"
                                    >
                                        <div class="flex space-x-2">
                                            <div class="inline-block md:hidden">
                                                <zen-inline-pill
                                                    :value="result.type"
                                                />
                                            </div>
                                            <div class="inline-block grow">
                                                <zen-grid-body-cell-primary-link
                                                    :to="{
                                                        name: 'ProjectContextDetailScreen',
                                                        params: {
                                                            projectKey:
                                                                currentEnvironment.projectKey,
                                                            environmentKey:
                                                                currentEnvironment.environmentKey,
                                                            contextUlid:
                                                                result.ulid,
                                                        },
                                                    }"
                                                >
                                                    {{ result.name }}
                                                </zen-grid-body-cell-primary-link>
                                            </div>
                                        </div>
                                        <zen-grid-mobile-detail>
                                            <zen-grid-mobile-detail-item
                                                label="Identifier"
                                            >
                                                <span class="font-mono">{{
                                                    result.identifier
                                                }}</span>
                                            </zen-grid-mobile-detail-item>
                                            <zen-grid-mobile-detail-item
                                                label="Segments"
                                                :value="
                                                    String(result.segments ?? 0)
                                                "
                                            />
                                            <zen-grid-mobile-detail-item
                                                label="Last Seen"
                                            >
                                                <zen-date-time
                                                    human
                                                    :value="result.last_seen_at"
                                                    blank="Never"
                                                />
                                            </zen-grid-mobile-detail-item>
                                        </zen-grid-mobile-detail>
                                    </zen-grid-body-cell>
                                    <zen-grid-body-cell alignment="center">
                                        <zen-inline-pill :value="result.type" />
                                    </zen-grid-body-cell>
                                    <zen-grid-body-cell>
                                        <span class="font-mono">
                                            {{ result.identifier }}
                                        </span>
                                    </zen-grid-body-cell>
                                    <zen-grid-body-cell
                                        alignment="center"
                                        :content="result.segments"
                                    />
                                    <zen-grid-body-cell>
                                        <zen-date-time
                                            human
                                            :value="result.last_seen_at"
                                            blank="Never"
                                        />
                                    </zen-grid-body-cell>
                                </zen-grid-body-row>
                            </zen-grid-body>
                        </zen-grid-table>
                        <template v-slot:filters>
                            <input
                                type="search"
                                id="search"
                                name="search"
                                v-model="filters.search"
                                autocomplete="off"
                                class="block w-full md:w-3/4 lg:w-1/2 rounded-md border-taupe-300 shadow-xs focus:border-taupe-300 focus:ring-taupe-500 sm:text-sm lg:text-base"
                                placeholder="Search for contexts..."
                                aria-label="Search for contexts"
                            />
                        </template>
                    </zen-grid>
                </zen-container>
            </div>
        </section>
    </main>
</template>

<script>
import ContextService from '@/services/context.service'

import { errorMessage } from '@/utils/notifications'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'

export default {
    name: 'ProjectContextsScreen',
    setup() {
        const { current: currentEnvironment } = useCurrentEnvironment()
        return { currentEnvironment }
    },
    computed: {
        hasResults() {
            return this.results != null && this.results.length > 0
        },
        sortedAndFilteredResults() {
            let filtered = this.results

            if (this.filters.search.length > 0) {
                const searchLower = this.filters.search.toLowerCase()
                filtered = filtered.filter((item) => {
                    return (
                        item.name.toLowerCase().includes(searchLower) ||
                        item.identifier.toLowerCase().includes(searchLower) ||
                        item.type.toLowerCase().includes(searchLower)
                    )
                })
            }

            return filtered.sort((a, b) =>
                b.last_seen_at.localeCompare(a.last_seen_at)
            )
        },
    },
    data() {
        return {
            filters: {
                search: '',
            },
            loading: true,
            results: null,
        }
    },
    methods: {
        refresh() {
            this.loading = true
            ContextService.list(
                this.currentEnvironment.projectKey,
                this.currentEnvironment.environmentKey
            ).then(
                (data) => {
                    this.results = data.data
                    this.loading = false
                },
                (error) => {
                    errorMessage(error)
                    this.loading = false
                }
            )
        },
    },
    mounted() {
        this.refresh()
    },
}
</script>
